// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import {produce} from 'immer';

import type {ThemesResource} from 'common/report/model-resources/themes';
import type {BookmarkTheme, Theme} from 'common/report/queries/app-api/themes';

import {MIXPANEL_DEFAULT_THEME_ID, STATIC_THEMES} from './constants';

/**
 * Finds a theme by id on the static list of themes or on project defined ones
 */
function findThemeById(id: string | number, themesResource: ThemesResource = null) {
  if (id === null || id === undefined) {
    return null;
  }

  return (
    STATIC_THEMES.find((staticTheme) => staticTheme.id === id) ??
    (themesResource ? themesResource.getOne(id).value : null)
  );
}

/**
 * Checks if the provided theme is an inline theme
 */
function isTheme(theme: Theme | {id: number | string}): theme is Theme {
  const themeData = (theme as Theme)?.data ?? null;
  return themeData && (`colors` in themeData || `darkColors` in themeData);
}

/** Resolves a theme by:
 * * retrieving it from the list of static themes if ID matches a static theme;
 * * retrieving it from the themes api resource if it's a saved theme (with an ID)
 * * returning the passed in theme if it's an inline theme (without an ID) */
export function resolveTheme({
  theme,
  themesResource,
  defaultThemeId = null,
}: {
  theme?: Theme | {id: number | string};
  themesResource?: ThemesResource;
  defaultThemeId?: number | string;
}): Theme {
  if (isTheme(theme)) {
    return theme;
  } else {
    return (
      findThemeById(theme?.id, themesResource) ?? findThemeById(defaultThemeId, themesResource) ?? STATIC_THEMES[0]
    );
  }
}

export function generateInlineTheme(theme: Theme, name: string, forMode?: `light` | `dark`): Theme {
  const data = produce(theme.data, (draft) => {
    // If the theme has only colors or darkColors, `forMode` moves the color list to the right key. This is so that a
    // user creating a theme will only edit colors for their current theme. Colors for the other mode will be synced
    // unless it is explicitly edited, since the other mode's colors are absent
    if (forMode === `light` && !draft.colors) {
      draft.colors = draft.darkColors;
      delete draft.darkColors;
    } else if (forMode === `dark` && !draft.darkColors) {
      draft.darkColors = draft.colors;
      delete draft.colors;
    }
  });

  return {
    id: undefined,
    type: theme.type,
    name,
    data,
  };
}

/** Get theme colors for the current light/dark mode. If colors for the active mode are not defined, fall back to the other one */
export function getColorsForMode(theme: Theme, darkMode: boolean) {
  const {colors, darkColors} = theme.data;
  return darkMode ? darkColors || colors : colors || darkColors;
}

export function getThemeTrackingProps(bookmarkTheme: BookmarkTheme): Record<string, any> {
  if (!bookmarkTheme) {
    bookmarkTheme = {id: MIXPANEL_DEFAULT_THEME_ID};
  }

  let theme;
  const themeKind = Number.isInteger(bookmarkTheme.id)
    ? `saved`
    : typeof bookmarkTheme.id === `string`
      ? `static`
      : `inline`;
  if (bookmarkTheme[`name`]) {
    // theme is inline or hydrated
    theme = bookmarkTheme as Theme;
  } else if (themeKind === `static`) {
    // retrieve the static theme's details. no need for the second themesResource arg, this just retrieves constants defined on the frontend
    theme = findThemeById(bookmarkTheme.id) ?? bookmarkTheme;
  } else {
    // this is a saved theme that has not been hydrated. we don't attempt to hydrate it here.
    theme = bookmarkTheme;
  }

  return {
    themeId: theme.id,
    themeName: theme.name,
    themeNumColors: theme.data?.colors?.length,
    themeNumDarkColors: theme.data?.darkColors?.length,
    themeColorsDifferent: theme.data && !isEqual(theme.data?.colors, theme.data?.darkColors),
    themeProjectSharing: theme.global_access_type,
    themeKind,
  };
}
